import { Box, Typography, useTheme, IconButton, Collapse } from '@mui/material';
import UpdateIcon from '@mui/icons-material/Update';
import ResponsiveTooltip from '../../ResponsiveTooltip';
import { alpha } from '@mui/material/styles';

import { useTranslation } from '../../../hooks/useTranslation';
import React from 'react';
import { usePricing } from '../../../hooks/usePricing';
import { convertTimestampToDate } from '../utils';
import type { Addons } from '../../../services/payment.types';
import { convertCentsToEuros, formatPrice } from '../../../services/transformers';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface AdditionalFeaturesProps {
    addons: Addons;
    isAddonPurchased: (addonName: string) => boolean;
    onAddonClick: (title: string, addonKey: keyof Addons) => void;
    onAddonToggle: (addonKey: keyof Addons) => void;
    getAddonValidUntil: (addonName: string) => string | undefined;
}

export const AdditionalFeatures: React.FC<AdditionalFeaturesProps> = ({
    addons,
    isAddonPurchased,
    onAddonClick,
    onAddonToggle,
    getAddonValidUntil
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { pricing } = usePricing();
    const [expanded, setExpanded] = React.useState(false);

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };

    // Debug logging for all addons
    React.useEffect(() => {
        console.log('AdditionalFeatures - All addons data:', {
            portfolioInsightsPurchased: isAddonPurchased('Portfolio Insights'),
            portfolioInsightsPurchasedDE: isAddonPurchased('Portfolio-Einblicke'),
            portfolioInsightsValidUntil: getAddonValidUntil('Portfolio Insights'),
            portfolioInsightsValidUntilDE: getAddonValidUntil('Portfolio-Einblicke'),
            addons,
        });
    }, [isAddonPurchased, getAddonValidUntil, addons]);

    const renderAddonOption = (_key: string, title: string, description: string, _icon: React.ReactNode, addonKey: keyof Addons) => {
        const isPurchased = isAddonPurchased(title);
        const isEnabled = addons[addonKey];
        const validUntil = getAddonValidUntil(title);

        // Debug logging
        console.log(`Addon: ${title}, isPurchased: ${isPurchased}, validUntil: ${validUntil}`);

        // Get dynamic price from the pricing service (API returns price in cents)
        const dynamicPriceInCents = pricing?.addons[addonKey]?.price;
        // Convert from cents to euros
        const priceInEuros = convertCentsToEuros(dynamicPriceInCents, 39.99);
        // Format price with currency symbol
        const displayPrice = formatPrice(priceInEuros);

        // Mark Portfolio Insights and Tax Optimization as coming soon
        const isComingSoon = addonKey === 'taxOptimization' || addonKey === 'portfolioInsights';

        // Format date in German format (DD.MM.YYYY) if available
        const formattedDate = validUntil ? 
            convertTimestampToDate(validUntil)?.toLocaleDateString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            }) : '';

        const handleClick = () => {
            if (isComingSoon) return;
            
            if (isPurchased) {
                // If the addon is already purchased and enabled, toggle it off
                if (isEnabled) {
                    onAddonToggle(addonKey);
                } else {
                    // If it's purchased but not enabled, show the dialog
                    onAddonClick(title, addonKey);
                }
            } else if (isEnabled) {
                // If enabled but not purchased, toggle it off
                onAddonToggle(addonKey);
            } else {
                // If not enabled, toggle it on
                onAddonToggle(addonKey);
            }
        };

        return (
            <Box
                onClick={isComingSoon ? undefined : handleClick}
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: { xs: 2, sm: 2.5 },
                    position: 'relative',
                    cursor: isComingSoon ? 'default' : 'pointer',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: isEnabled ? 'primary.main' : 'transparent',
                    backgroundColor: isComingSoon ? 'rgba(0,0,0,0.03)' : 'background.paper',
                    minHeight: '64px',
                    opacity: isComingSoon ? 0.7 : 1,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: isEnabled
                        ? '0 4px 20px rgba(25, 118, 210, 0.15)'
                        : '0 1px 3px rgba(0,0,0,0.05)',
                    '&:hover': {
                        backgroundColor: isComingSoon ? 'rgba(0,0,0,0.03)' : 'action.hover',
                        transform: isComingSoon ? 'none' : 'translateY(-1px)',
                        boxShadow: isEnabled
                            ? '0 8px 30px rgba(25, 118, 210, 0.25)'
                            : '0 4px 12px rgba(0,0,0,0.1)',
                    },
                    ...(isPurchased && isComingSoon && {
                        '& h6': { color: '#757575 !important' },
                        '& p': { color: '#757575 !important' },
                        '& h5': { color: '#757575 !important' },
                        '& span': { color: '#757575 !important' },
                        '& .MuiTypography-root': { color: '#757575 !important' }
                    }),
                }}
            >
                {/* Addon Name with Info Icon */}
                <Box sx={{ 
                    flex: '1 0 60%',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 1,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                }}>
                    <Typography variant="h6" fontWeight="bold" sx={{
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                        color: isComingSoon ? 'text.secondary' : 'inherit'
                    }}>
                        {title}
                    </Typography>
                    <ResponsiveTooltip
                        title={
                            <Box sx={{ width: 300, p: 1.5, maxWidth: 300 }}>
                                <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                                    {description}
                                </Typography>
                            </Box>
                        }
                        arrow
                        placement="bottom"
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    bgcolor: (theme) => alpha(theme.palette.background.paper, 0.9),
                                    backdropFilter: 'blur(5px)',
                                    color: theme.palette.text.primary,
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                                    border: '1px solid',
                                    borderColor: theme.palette.divider,
                                    borderRadius: 1,
                                    p: 0,
                                    maxWidth: 'none',
                                    fontSize: '0.9rem',
                                    '& .MuiTooltip-arrow': {
                                        color: (theme) => alpha(theme.palette.background.paper, 0.9),
                                    }
                                }
                            }
                        }}
                    >
                        <InfoOutlinedIcon 
                            sx={{ 
                                fontSize: '1rem', 
                                color: isComingSoon ? 'text.disabled' : 'text.secondary',
                                cursor: 'help'
                            }} 
                        />
                    </ResponsiveTooltip>
                </Box>
                
                {/* Addon Price or Coming Soon Badge */}
                <Box sx={{ 
                    flex: '1 0 40%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap'
                }}>
                    {isComingSoon ? (
                        <ResponsiveTooltip title="Demnächst verfügbar" arrow placement="top">
                            <Box sx={{
                                display: 'flex',
                                p: 0.5,
                                borderRadius: '4px',
                                backgroundColor: 'rgba(0,0,0,0.08)',
                                color: 'text.secondary',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 28,
                                height: 28,
                            }}>
                                <UpdateIcon sx={{ fontSize: '1rem' }} />
                            </Box>
                        </ResponsiveTooltip>
                    ) : isPurchased ? (
                        <Typography variant="body2" sx={{ 
                            fontSize: '0.85rem',
                            color: theme.palette.success.main
                        }}>
                            Gültig bis {formattedDate}
                        </Typography>
                    ) : (
                        <Typography variant="h5" fontWeight="bold" sx={{
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            color: theme.palette.text.primary
                        }}>
                            {displayPrice}
                        </Typography>
                    )}
                </Box>
                
                {/* Plus icon removed from selected addons */}
            </Box>
        );
    };

    return (
        <Box sx={{
            p: 4,
            mb: 3,
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 2,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        }}>
            <Box 
                sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    '&:hover': {
                        opacity: 0.8
                    }
                }}
                onClick={handleExpandClick}
            >
                <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
                    Optionale Addons hinzufügen
                </Typography>
                <IconButton
                    sx={{
                        p: 1,
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                            transform: 'scale(1.05)',
                        },
                        width: 32,
                        height: 32
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleExpandClick();
                    }}
                >
                    {expanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                </IconButton>
            </Box>
            
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box sx={{ mt: 3, px: { xs: 2, sm: 3 } }}>
                    {/* Table Headers */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        py: 2,
                        px: 2,
                        mb: 2,
                        alignItems: 'center'
                    }}>
                        <Typography 
                            variant="subtitle2" 
                            sx={{ 
                                fontWeight: 'bold', 
                                flex: '1 0 60%',
                                fontSize: { xs: '1rem', sm: '1.1rem' }
                            }}
                        >
                            {t('plans.tableHeaders.addon')}
                        </Typography>
                        <Typography 
                            variant="subtitle2" 
                            sx={{ 
                                fontWeight: 'bold', 
                                flex: '1 0 40%',
                                textAlign: 'right',
                                fontSize: { xs: '1rem', sm: '1.1rem' }
                            }}
                        >
                            {t('plans.tableHeaders.price')}
                        </Typography>
                    </Box>
                    
                    {/* Addon Items */}
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {renderAddonOption(
                            'Portfolio Insights',
                            t('plans.addons.portfolioInsights.title'),
                            t('plans.addons.portfolioInsights.description'),
                            null,
                            'portfolioInsights'
                        )}

                        {renderAddonOption(
                            'Tax Optimization',
                            t('plans.addons.taxOptimization.title'),
                            t('plans.addons.taxOptimization.description'),
                            null,
                            'taxOptimization'
                        )}
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};
